import { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { IconCamera, IconLogoEdit, IconMemberRemove, IconSelectArrow } from '../../../Components/Common/Icons';
import { AVATAR_ALLOWED_TYPES } from '../../../Helpers/formValidation';
import { useLanguage } from '../../../localization/useLanguage';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '../../../ui/Dropdown';
import { cn } from '../../../ui/lib/utils';
import ContactAvatar from '../../../Components/WebChat/Common/ContactList/ContactAvatar';
import { DISAPPEARING_DURATIONS, GROUP_DESCRIPTION_MAX_LENGTH, GROUP_NAME_MAX_LENGTH } from '../constants';
import {
    formatContactPhone,
    getContactName,
    sortContactsByName,
} from '../../../Components/WebChat/Common/ContactList/contactHelpers';
import dialogStyles from '../../../Components/WebChat/Common/ContactList/dialog.module.scss';
import styles from './index.module.scss';

// Common rule 13: JPG (JPEG), PNG, WebP, SVG, HEIC/HEIF, AVIF.
const LOGO_ACCEPT = [...AVATAR_ALLOWED_TYPES, '.jpg', '.jpeg', '.png', '.webp', '.svg', '.heic', '.heif', '.avif'].join(
    ',',
);

const GroupLogoField = ({ preview, error, onPick }) => {
    const { t } = useLanguage();
    const fileInputRef = useRef(null);
    const handleOpenPicker = () => fileInputRef.current?.click();

    const handleFileChange = event => {
        const [file] = event.target.files || [];
        // Cleared so picking the same file again after an error still fires a change.
        event.target.value = '';
        if (file) onPick(file);
    };

    return (
        <>
            <button
                type="button"
                className={styles.logo}
                onClick={handleOpenPicker}
                aria-label={t('createChat.CHOOSE_GROUP_LOGO')}
            >
                <span className={styles.logoShot}>{preview ? <img src={preview} alt="" /> : <IconCamera />}</span>
                <span className={styles.logoBadge}>
                    <IconLogoEdit />
                </span>
            </button>
            <button type="button" className={styles.logoCaption} onClick={handleOpenPicker}>
                {t('createChat.ADD_GROUP_LOGO')}
            </button>
            <input ref={fileInputRef} type="file" accept={LOGO_ACCEPT} hidden onChange={handleFileChange} />
            {error && <p className={cn(dialogStyles.error, styles.logoError)}>{error}</p>}
        </>
    );
};

GroupLogoField.propTypes = {
    preview: PropTypes.string,
    error: PropTypes.string,
    onPick: PropTypes.func.isRequired,
};

const DisappearingField = ({ isOn, durationDays, onChange }) => {
    const { t } = useLanguage();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    // The menu renders inside the field so it stays within the modal dialog's top layer.
    const [menuHost, setMenuHost] = useState(null);
    const selected = DISAPPEARING_DURATIONS.find(({ days }) => days === durationDays);

    const handleMenuEscape = event => {
        event.preventDefault();
        setIsMenuOpen(false);
    };

    return (
        <>
            <div className={styles.toggleRow}>
                <span className={styles.toggleLabel}>
                    <b>{t('createChat.DISAPPEARING_MESSAGES')}</b>
                    <span>{t('createChat.DISAPPEARING_DESCRIPTION')}</span>
                </span>
                <button
                    type="button"
                    role="switch"
                    className={styles.switch}
                    aria-checked={isOn}
                    aria-label={t('createChat.DISAPPEARING_MESSAGES')}
                    onClick={() => onChange({ isDisappearing: !isOn })}
                />
            </div>
            {isOn && (
                <div ref={setMenuHost} className={cn(dialogStyles.field, styles.durationField)}>
                    <label className={dialogStyles.label} htmlFor="create-chat-group-duration">
                        {t('createChat.DURATION')}
                    </label>
                    <DropdownMenu modal={false} open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                        <DropdownMenuTrigger asChild>
                            <button
                                id="create-chat-group-duration"
                                type="button"
                                className={cn(dialogStyles.ctrl, styles.selectTrigger)}
                            >
                                <span>{selected && t(selected.labelKey)}</span>
                                <IconSelectArrow className={styles.selectArrow} />
                            </button>
                        </DropdownMenuTrigger>
                        {/* Esc closes only the menu; left alone it would also cancel the dialog. */}
                        <DropdownMenuContent
                            container={menuHost}
                            align="start"
                            className={styles.selectMenu}
                            onEscapeKeyDown={handleMenuEscape}
                        >
                            <DropdownMenuRadioGroup
                                value={String(durationDays)}
                                onValueChange={value => onChange({ durationDays: Number(value) })}
                            >
                                {DISAPPEARING_DURATIONS.map(({ days, labelKey }) => (
                                    <DropdownMenuRadioItem key={days} value={String(days)} hideIndicator>
                                        {t(labelKey)}
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            )}
        </>
    );
};

DisappearingField.propTypes = {
    isOn: PropTypes.bool.isRequired,
    durationDays: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
};

const MemberList = ({ members, onRemove }) => {
    const { t } = useLanguage();

    return (
        <>
            {/* The user is a member too, so the count is one more than the rows. */}
            <p className={styles.membersCaption}>{t('createChat.MEMBERS', { count: members.length + 1 })}</p>
            <div className={styles.members}>
                {members.map(contact => {
                    const name = getContactName(contact);
                    return (
                        <div key={contact.userId} className={styles.member}>
                            <ContactAvatar contact={contact} />
                            <span className={styles.memberText}>
                                <b title={name}>{name}</b>
                                <span>{formatContactPhone(contact)}</span>
                            </span>
                            <button
                                type="button"
                                className={styles.memberRemove}
                                onClick={() => onRemove(contact)}
                                aria-label={t('createChat.REMOVE_MEMBER', { name })}
                            >
                                <IconMemberRemove />
                            </button>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

MemberList.propTypes = {
    members: PropTypes.arrayOf(PropTypes.object).isRequired,
    onRemove: PropTypes.func.isRequired,
};

/**
 * New Group pop-up, step 2 (FR60 screen 60.4): logo, name, description, disappearing
 * messages and the member list, then Create Group.
 *
 * @param {object} details Draft: name, description, logoPreview, isDisappearing, durationDays.
 * @param {object} errors Field errors: logo, name.
 * @param {Map} selected Selected contacts keyed by userId.
 * @param {boolean} isSubmitting Disables the actions while the group is being created.
 * @param {Function} onChange Merges a patch into the draft.
 * @param {Function} onLogoPick Called with the chosen file.
 * @param {Function} onNameBlur Validates the name when the field is left.
 * @param {Function} onRemoveMember Drops a member from the group being created.
 * @param {Function} onBack Returns to step 1 with the selection kept.
 * @param {Function} onCreate Creates the group.
 */
const DetailsStep = ({
    details,
    errors,
    selected,
    isSubmitting,
    onChange,
    onLogoPick,
    onNameBlur,
    onRemoveMember,
    onBack,
    onCreate,
}) => {
    const { t } = useLanguage();
    const members = useMemo(() => sortContactsByName([...selected.values()]), [selected]);
    const canCreate = details.name.trim() !== '' && members.length > 0 && !isSubmitting;

    return (
        <>
            <GroupLogoField preview={details.logoPreview} error={errors.logo} onPick={onLogoPick} />

            <div className={cn(dialogStyles.field, errors.name && dialogStyles.invalid)}>
                <label className={dialogStyles.label} htmlFor="create-chat-group-name">
                    {t('createChat.GROUP_NAME')}
                </label>
                <div className={dialogStyles.ctrl}>
                    <input
                        id="create-chat-group-name"
                        autoComplete="off"
                        value={details.name}
                        maxLength={GROUP_NAME_MAX_LENGTH}
                        placeholder={t('createChat.GROUP_NAME_PLACEHOLDER')}
                        onChange={event => onChange({ name: event.target.value })}
                        onBlur={onNameBlur}
                    />
                </div>
                {errors.name && <p className={dialogStyles.error}>{errors.name}</p>}
            </div>

            <div className={dialogStyles.field}>
                <label className={dialogStyles.label} htmlFor="create-chat-group-description">
                    {t('createChat.DESCRIPTION')}
                </label>
                <div className={dialogStyles.textareaWrap}>
                    <textarea
                        id="create-chat-group-description"
                        value={details.description}
                        maxLength={GROUP_DESCRIPTION_MAX_LENGTH}
                        placeholder={t('createChat.GROUP_DESCRIPTION_PLACEHOLDER')}
                        onChange={event => onChange({ description: event.target.value })}
                    />
                    <span className={dialogStyles.count}>
                        {details.description.length}/{GROUP_DESCRIPTION_MAX_LENGTH}
                    </span>
                </div>
            </div>

            <div className={styles.separator} />
            <DisappearingField isOn={details.isDisappearing} durationDays={details.durationDays} onChange={onChange} />
            <div className={styles.separator} />

            <MemberList members={members} onRemove={onRemoveMember} />

            <div className={dialogStyles.actions}>
                <button
                    type="button"
                    className={cn(dialogStyles.button, dialogStyles.outline)}
                    disabled={isSubmitting}
                    onClick={onBack}
                >
                    {t('createChat.BACK')}
                </button>
                <button
                    type="button"
                    className={cn(dialogStyles.button, dialogStyles.primary)}
                    disabled={!canCreate}
                    onClick={onCreate}
                >
                    {t('createChat.CREATE_GROUP')}
                </button>
            </div>
        </>
    );
};

DetailsStep.propTypes = {
    details: PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        logoPreview: PropTypes.string,
        isDisappearing: PropTypes.bool.isRequired,
        durationDays: PropTypes.number.isRequired,
    }).isRequired,
    errors: PropTypes.shape({ logo: PropTypes.string, name: PropTypes.string }).isRequired,
    selected: PropTypes.instanceOf(Map).isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    onLogoPick: PropTypes.func.isRequired,
    onNameBlur: PropTypes.func.isRequired,
    onRemoveMember: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
    onCreate: PropTypes.func.isRequired,
};

export default DetailsStep;
